/**
 * Lightweight XOR-based permission decryption.
 * Mirrors the backend's encryptPermBundle — same seed, same mixing, fully synchronous.
 */

function buildSeed(userId, iat) {
    return `${userId}${iat}stride`;
}

function keyByte(seed, index) {
    return seed.charCodeAt(index % seed.length) ^ ((index * 13 + 97) & 0xff);
}

/**
 * Parses the payload of a JWT without verifying the signature.
 * Used only to extract userId (sub) and issued-at (iat) for key derivation.
 */
export function parseJWTPayload(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
}

/**
 * XOR-decrypts a base64 permissions blob produced by the backend.
 * Synchronous — no Web Crypto API, no async needed.
 *
 * @param {string} encryptedBase64
 * @param {string} userId  - Auth0 user sub
 * @param {number} iat     - JWT issued-at (seconds)
 * @returns {Object} Decrypted permissions bundle { permissions, route_permission, feature_permission, feature_restriction }
 */
export function decryptPermBundle(encryptedBase64, userId, iat) {
    if (!encryptedBase64) return null;
    const seed = buildSeed(userId, iat);
    const raw = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));
    const result = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
        result[i] = raw[i] ^ keyByte(seed, i);
    }
    return JSON.parse(new TextDecoder().decode(result));
}

// ---------------------------------------------------------------------------
// Integration request / response encryption
// ---------------------------------------------------------------------------
// Mirrors server/src/utils/permEncrypt.js  encryptForClient / decryptFromClient.
// Same seed formula: userId + iat + "stride".

function xorBase64(base64, seed) {
    const raw = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) {
        out[i] = raw[i] ^ keyByte(seed, i);
    }
    return out;
}

function objectToBase64Enc(data, seed) {
    const json = JSON.stringify(data);
    const encoded = new TextEncoder().encode(json);
    const out = new Uint8Array(encoded.length);
    for (let i = 0; i < encoded.length; i++) {
        out[i] = encoded[i] ^ keyByte(seed, i);
    }
    // btoa requires a binary string
    return btoa(String.fromCharCode(...out));
}

/**
 * Encrypts a request payload for sending to the server.
 * Returns { __enc: "<base64>" } — what the server expects as req.body.
 *
 * @param {*}      data  - Anything JSON-serialisable
 * @param {string} token - Raw JWT string (used to derive userId + iat)
 */
export function encryptForServer(data, token) {
    try {
        const { sub: userId, iat } = parseJWTPayload(token);
        const seed = buildSeed(userId, iat);
        return { __enc: objectToBase64Enc(data, seed) };
    } catch {
        // Fallback: send raw (will be ignored if server requires __enc)
        return data;
    }
}

/**
 * Decrypts an encrypted response sent by the server.
 * Expects the server to have sent { __enc: "<base64>" } inside the data field.
 *
 * @param {string} encBase64 - base64 ciphertext (response.data.__enc)
 * @param {string} token     - Raw JWT string (to derive userId + iat)
 * @returns {*}              Parsed object, or null on failure
 */
export function decryptFromServer(encBase64, token) {
    try {
        const { sub: userId, iat } = parseJWTPayload(token);
        const seed = buildSeed(userId, iat);
        const decrypted = xorBase64(encBase64, seed);
        return JSON.parse(new TextDecoder().decode(decrypted));
    } catch {
        return null;
    }
}
